/**
 * The client card's people, tax files and notes.
 *
 * Extracted from ClientCard so that file stays readable — these are three independent panels
 * with their own forms, and inlining them would double the length of the card.
 *
 * The pinned note comes first on purpose. It is the thing to know before phoning a client, and
 * a note that has to be scrolled to is a note nobody reads.
 */
import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AUTHORITIES, formatDate, formatIsraeliPhone, type AuthorityKey } from "@onecpa/shared";
import {
  Badge,
  Button,
  Checkbox,
  Dialog,
  EmptyState,
  Field,
  IconButton,
  Input,
  Panel,
  Select,
  Skeleton,
  Textarea,
} from "@onecpa/ui";
import { api, type ClientContact } from "../api/client.js";
import { useRole, useSession } from "../app/session.js";

export function ClientPeople({ clientId }: { clientId: string }) {
  const session = useSession();
  const { isManager } = useRole();
  const queryClient = useQueryClient();

  const [addingContact, setAddingContact] = useState(false);
  const [addingFile, setAddingFile] = useState(false);
  const [noteDraft, setNoteDraft] = useState("");
  const [noteVisible, setNoteVisible] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ["client", "detail", session.orgId, clientId],
    queryFn: () => api.clients.detail(session.orgId, clientId),
  });

  const invalidate = async () => {
    await queryClient.invalidateQueries({ queryKey: ["client", "detail"] });
  };

  const addNote = useMutation({
    mutationFn: () =>
      api.clients.addNote(session.orgId, clientId, {
        body: noteDraft.trim(),
        clientVisible: noteVisible,
      }),
    onSuccess: async () => {
      await invalidate();
      setNoteDraft("");
      setNoteVisible(false);
      session.toast("ההערה נוספה");
    },
    onError: (error: Error) => session.toast(error.message, "danger"),
  });

  const togglePin = useMutation({
    mutationFn: (input: { id: string; isPinned: boolean }) =>
      api.clients.updateNote(session.orgId, clientId, input.id, { isPinned: input.isPinned }),
    onSuccess: invalidate,
    onError: (error: Error) => session.toast(error.message, "danger"),
  });

  const removeContact = useMutation({
    mutationFn: (id: string) => api.clients.removeContact(session.orgId, clientId, id),
    onSuccess: async () => {
      await invalidate();
      session.toast("איש הקשר הוסר");
    },
    onError: (error: Error) => session.toast(error.message, "danger"),
  });

  const makePrimary = useMutation({
    mutationFn: (id: string) =>
      api.clients.updateContact(session.orgId, clientId, id, { isPrimary: true }),
    onSuccess: async () => {
      await invalidate();
      session.toast("איש הקשר הראשי עודכן");
    },
    onError: (error: Error) => session.toast(error.message, "danger"),
  });

  if (isLoading || !data) {
    return (
      <div className="grid gap-4 lg:grid-cols-2">
        <Panel title="אנשי קשר">
          <div className="flex flex-col gap-2 px-3.5 py-3">
            <Skeleton className="h-4 w-48" />
            <Skeleton className="h-4 w-40" />
          </div>
        </Panel>
      </div>
    );
  }

  const primary = data.contacts.find((contact) => contact.isPrimary);

  return (
    <>
      <div className="grid gap-4 lg:grid-cols-2">
        <Panel
          title="אנשי קשר"
          action={
            <Button size="sm" icon="plus" onClick={() => setAddingContact(true)}>
              הוספה
            </Button>
          }
        >
          {data.contacts.length > 0 ? (
            <ul className="divide-y divide-line-soft">
              {data.contacts.map((contact) => (
                <li key={contact.id} className="flex flex-wrap items-center gap-2 px-3.5 py-2.5">
                  <span className="min-w-32 flex-1">
                    <span className="block text-sm font-semibold text-ink">
                      {contact.fullName}
                    </span>
                    <span className="block text-2xs text-ink-faint">
                      {contact.role ?? "—"}
                    </span>
                  </span>

                  <span className="flex min-w-40 flex-col text-2xs text-ink-muted">
                    {contact.email && <bdi dir="ltr">{contact.email}</bdi>}
                    {contact.phoneE164 && (
                      <bdi className="tnum" dir="ltr">
                        {formatIsraeliPhone(contact.phoneE164)}
                      </bdi>
                    )}
                  </span>

                  {contact.isPrimary ? (
                    <Badge tone="success">מקבל תזכורות</Badge>
                  ) : (
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => makePrimary.mutate(contact.id)}
                    >
                      הגדר כראשי
                    </Button>
                  )}
                  {contact.canSign && <Badge tone="info">מורשה חתימה</Badge>}

                  {isManager && !contact.isPrimary && (
                    <IconButton
                      icon="plus"
                      label={`הסרת ${contact.fullName}`}
                      className="rotate-45"
                      onClick={() => removeContact.mutate(contact.id)}
                    />
                  )}
                </li>
              ))}
            </ul>
          ) : (
            <EmptyState
              title="אין אנשי קשר"
              description="בלי איש קשר ראשי לא יישלחו תזכורות ללקוח הזה."
              icon="users"
              action={
                <Button variant="primary" onClick={() => setAddingContact(true)}>
                  הוספת איש קשר
                </Button>
              }
            />
          )}
          {data.contacts.length > 0 && !primary && (
            <p className="border-t border-line-soft bg-warning-bg px-3.5 py-2 text-2xs text-warning-ink">
              אין איש קשר ראשי — תזכורות ללקוח הזה לא יישלחו לאף אחד.
            </p>
          )}
        </Panel>

        <Panel
          title="תיקים ברשויות"
          action={
            <Button size="sm" icon="plus" onClick={() => setAddingFile(true)}>
              הוספה
            </Button>
          }
        >
          {data.taxFiles.length > 0 ? (
            <ul className="divide-y divide-line-soft">
              {data.taxFiles.map((file) => (
                <li key={file.id} className="flex flex-wrap items-center gap-2 px-3.5 py-2.5">
                  <span className="min-w-32 flex-1 text-xs text-ink">
                    {AUTHORITIES[file.authority as AuthorityKey]?.label ?? file.authority}
                  </span>
                  <bdi className="tnum text-xs text-ink" dir="ltr">
                    {file.fileNumber}
                  </bdi>
                  {file.isPrimary && <Badge tone="info">ראשי</Badge>}
                  {file.status === "closed" ? (
                    <Badge tone="neutral">
                      נסגר{file.closedOn ? ` ${formatDate(file.closedOn)}` : ""}
                    </Badge>
                  ) : (
                    <Badge tone="success">פעיל</Badge>
                  )}
                </li>
              ))}
            </ul>
          ) : (
            <EmptyState title="אין תיקים רשומים" icon="landmark" />
          )}
        </Panel>

        <Panel title="הערות" className="lg:col-span-2">
          <div className="flex flex-col gap-2 border-b border-line-soft px-3.5 py-3">
            <Textarea
              value={noteDraft}
              onChange={(event) => setNoteDraft(event.target.value)}
              placeholder="מה כדאי לדעת על הלקוח הזה?"
              aria-label="הערה חדשה"
              className="min-h-16"
            />
            <div className="flex flex-wrap items-center gap-3">
              <Button
                variant="primary"
                size="sm"
                disabled={noteDraft.trim().length === 0}
                loading={addNote.isPending}
                onClick={() => addNote.mutate()}
              >
                הוספת הערה
              </Button>
              <Checkbox
                label="גלוי ללקוח בפורטל"
                checked={noteVisible}
                onChange={(event) => setNoteVisible(event.target.checked)}
              />
              <span className="text-2xs text-ink-faint">
                הערות הן פנימיות כברירת מחדל — סמנו רק אם ברצונכם שהלקוח יראה אותן.
              </span>
            </div>
          </div>

          {data.notes.length > 0 ? (
            <ul className="divide-y divide-line-soft">
              {data.notes.map((note) => (
                <li key={note.id} className="px-3.5 py-2.5">
                  <div className="flex items-start gap-2">
                    <p className="flex-1 text-sm whitespace-pre-line text-ink">{note.body}</p>
                    {note.isPinned && <Badge tone="warning">נעוץ</Badge>}
                    {note.clientVisible && <Badge tone="info">גלוי ללקוח</Badge>}
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => togglePin.mutate({ id: note.id, isPinned: !note.isPinned })}
                    >
                      {note.isPinned ? "בטל נעיצה" : "נעץ"}
                    </Button>
                  </div>
                  <p className="mt-1 text-2xs text-ink-faint">
                    {note.authorName ?? "המערכת"} ·{" "}
                    <bdi className="tnum">{formatDate(note.createdAt)}</bdi>
                  </p>
                </li>
              ))}
            </ul>
          ) : (
            <EmptyState title="אין הערות" icon="file-text" />
          )}
        </Panel>
      </div>

      <ContactDialog
        open={addingContact}
        onClose={() => setAddingContact(false)}
        clientId={clientId}
        hasPrimary={Boolean(primary)}
      />
      <TaxFileDialog open={addingFile} onClose={() => setAddingFile(false)} clientId={clientId} />
    </>
  );
}

function ContactDialog({
  open,
  onClose,
  clientId,
  hasPrimary,
}: {
  open: boolean;
  onClose(): void;
  clientId: string;
  hasPrimary: boolean;
}) {
  const session = useSession();
  const queryClient = useQueryClient();
  const [fullName, setFullName] = useState("");
  const [role, setRole] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  // The first contact on a client must be primary, or nothing will ever be sent.
  const [isPrimary, setIsPrimary] = useState(!hasPrimary);
  const [canSign, setCanSign] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const create = useMutation({
    mutationFn: () =>
      api.clients.addContact(session.orgId, clientId, {
        fullName: fullName.trim(),
        isPrimary,
        canSign,
        ...(role.trim() ? { role: role.trim() } : {}),
        ...(email.trim() ? { email: email.trim() } : {}),
        ...(phone.trim() ? { phoneE164: phone.trim() } : {}),
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["client", "detail"] });
      session.toast("איש הקשר נוסף");
      setFullName("");
      setRole("");
      setEmail("");
      setPhone("");
      onClose();
    },
    onError: (mutationError: Error) => setError(mutationError.message),
  });

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title="איש קשר חדש"
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            ביטול
          </Button>
          <Button
            variant="primary"
            disabled={fullName.trim().length === 0}
            loading={create.isPending}
            onClick={() => {
              setError(null);
              create.mutate();
            }}
          >
            הוספה
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-3.5">
        <Field label="שם מלא" required>
          {({ id }) => (
            <Input id={id} value={fullName} onChange={(event) => setFullName(event.target.value)} />
          )}
        </Field>
        <Field label="תפקיד" help="לדוגמה: בעל העסק, מנהלת חשבונות אצל הלקוח">
          {({ id }) => (
            <Input id={id} value={role} onChange={(event) => setRole(event.target.value)} />
          )}
        </Field>
        <Field label="דוא״ל">
          {({ id }) => (
            <Input
              id={id}
              type="email"
              dir="ltr"
              value={email}
              onChange={(event) => setEmail(event.target.value)}
            />
          )}
        </Field>
        <Field label="טלפון" help="בפורמט בינלאומי, לדוגמה +972541234567">
          {({ id }) => (
            <Input
              id={id}
              dir="ltr"
              value={phone}
              onChange={(event) => setPhone(event.target.value)}
            />
          )}
        </Field>
        <Checkbox
          label="מקבל את התזכורות (איש קשר ראשי)"
          checked={isPrimary}
          onChange={(event) => setIsPrimary(event.target.checked)}
        />
        <Checkbox
          label="מורשה לחתום על מסמכים"
          checked={canSign}
          onChange={(event) => setCanSign(event.target.checked)}
        />
        {isPrimary && hasPrimary && (
          <p className="text-2xs text-ink-faint">
            איש הקשר הראשי הקיים יוסר מהתפקיד — לכל לקוח יש ראשי אחד בלבד.
          </p>
        )}
        {error && (
          <p role="alert" className="text-xs font-semibold text-red-ink">
            {error}
          </p>
        )}
      </div>
    </Dialog>
  );
}

function TaxFileDialog({
  open,
  onClose,
  clientId,
}: {
  open: boolean;
  onClose(): void;
  clientId: string;
}) {
  const session = useSession();
  const queryClient = useQueryClient();
  const [authority, setAuthority] = useState<AuthorityKey>("vat");
  const [fileNumber, setFileNumber] = useState("");
  const [status, setStatus] = useState("active");
  const [error, setError] = useState<string | null>(null);

  const create = useMutation({
    mutationFn: () =>
      api.clients.addTaxFile(session.orgId, clientId, {
        authority,
        fileNumber: fileNumber.trim(),
        status,
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["client", "detail"] });
      session.toast("התיק נוסף");
      setFileNumber("");
      onClose();
    },
    onError: (mutationError: Error) => setError(mutationError.message),
  });

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title="תיק חדש ברשות"
      size="sm"
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            ביטול
          </Button>
          <Button
            variant="primary"
            disabled={fileNumber.trim().length === 0}
            loading={create.isPending}
            onClick={() => {
              setError(null);
              create.mutate();
            }}
          >
            הוספה
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-3.5">
        <Field label="רשות" required>
          {({ id }) => (
            <Select
              id={id}
              value={authority}
              onChange={(event) => setAuthority(event.target.value as AuthorityKey)}
            >
              {Object.entries(AUTHORITIES).map(([key, meta]) => (
                <option key={key} value={key}>
                  {meta.label}
                </option>
              ))}
            </Select>
          )}
        </Field>
        <Field label="מספר תיק" required>
          {({ id }) => (
            <Input
              id={id}
              dir="ltr"
              value={fileNumber}
              onChange={(event) => setFileNumber(event.target.value)}
            />
          )}
        </Field>
        <Field label="מצב">
          {({ id }) => (
            <Select id={id} value={status} onChange={(event) => setStatus(event.target.value)}>
              <option value="active">פעיל</option>
              <option value="closed">נסגר</option>
              <option value="suspended">מושהה</option>
            </Select>
          )}
        </Field>
        {error && (
          <p role="alert" className="text-xs font-semibold text-red-ink">
            {error}
          </p>
        )}
      </div>
    </Dialog>
  );
}

/** Re-exported so ClientCard can type its tab content without importing the API types. */
export type { ClientContact };
